import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import { 
  addDoc, 
  collection, 
  serverTimestamp,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  orderBy,
  limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import './CreateDesignPage.css';
import ditherArizona from '../assets/images/dither-arizona.png';
import { US_STATES, getCountiesForState } from '../data/us-locations';
import { streamCamoDesign } from '../utils/camoStreamClient';
import SimpleMarkdownRenderer from '../components/ui/SimpleMarkdownRenderer';
import CamoEffectivenessReport from '../components/CamoEffectivenessReport';
import VariantToggle from '../components/VariantToggle';
import { scoreCamoEffectiveness } from '../utils/camoEffectivenessScore';
import { sanitizePublicEnvironmentAnalysis } from '../utils/publicEnvironmentAnalysis';
import { triggerDesignRefinement } from '../utils/cloudFunctionUrls';
import { scrollToTop } from '../utils/scrollUtils';

const LOADING_STEPS = [
  { id: 'uploading_photo', label: 'Upload' },
  { id: 'uploading_to_storage', label: 'Store' },
  { id: 'generating_designs', label: 'Analyze' },
  { id: 'ai_generation', label: 'Generate' },
  { id: 'uploading_designs', label: 'Save' },
  { id: 'creating_document', label: 'Record' },
  { id: 'finalizing', label: 'Finish' },
];

function getLoadingStepStatus(stepId, processingStep, allComplete = false) {
  if (allComplete) return 'completed';
  const order = LOADING_STEPS.map((s) => s.id);
  const currentIdx = order.indexOf(processingStep);
  const stepIdx = order.indexOf(stepId);
  if (currentIdx === -1) return stepIdx === 0 ? 'active' : 'pending';
  if (stepIdx < currentIdx) return 'completed';
  if (stepIdx === currentIdx) return 'active';
  return 'pending';
}

function getLoadingProgressPercent(processingStep, allComplete = false) {
  if (allComplete) return 100;
  const order = LOADING_STEPS.map((s) => s.id);
  const currentIdx = order.indexOf(processingStep);
  if (currentIdx === -1) return 4;
  return Math.round(((currentIdx + 1) / order.length) * 100);
}

// Helper function to convert data URL to file
const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

// Enhanced sanitize function to prevent executable injection attacks
function sanitizeInput(input) {
  if (typeof input !== 'string') return '';
  
  // Remove all HTML tags and scripts
  let sanitized = input.replace(/<[^>]*>?/gm, '');
  
  // Remove JavaScript code patterns
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  sanitized = sanitized.replace(/eval\s*\(/gi, '');
  sanitized = sanitized.replace(/function\s*\(/gi, '');
  
  // Remove potentially dangerous characters and patterns
  sanitized = sanitized.replace(/[<>{}]/g, '');
  sanitized = sanitized.replace(/\\/g, '');
  sanitized = sanitized.replace(/`/g, '');
  
  // Keep original spacing - don't normalize multiple spaces
  sanitized = sanitized.trim();
  
  return sanitized;
}

// Add at the top, after imports
const ADJECTIVES = [
  'Silent', 'Shadow', 'Mossy', 'Swift', 'Dusky', 'Hidden', 'Wild', 'Blazing', 'Frosty', 'Golden',
  'Clever', 'Coyote', 'Sage', 'Ghost', 'Iron', 'Crimson', 'Emerald', 'Sandy', 'Stormy', 'Lone',
  'Copper', 'Granite', 'Shady', 'Sunlit', 'Windy', 'Stealthy', 'Dappled', 'Speckled', 'Misty', 'Bold',
  'Twilight', 'Amber', 'Silent', 'Gritty', 'Dusky', 'Feral', 'Bramble', 'Faded', 'Grove', 'Hushed',
  'Pebbled', 'Rugged', 'Tawny', 'Vivid', 'Wandering', 'Breezy', 'Flecked', 'Fallow', 'Glimmering', 'Muted',
  'Nocturnal', 'Patchy', 'Pebbly', 'Rustic', 'Sable', 'Tangled', 'Vernal', 'Wisp', 'Zephyr', 'Cloaked',
  'Flicker', 'Gnarled', 'Hollow', 'Jagged', 'Lush', 'Mottled', 'Obscure', 'Quaking', 'Reedy', 'Sable',
  'Tawny', 'Umber', 'Verdant', 'Wraith', 'Yonder', 'Brindle', 'Crested', 'Drowsy', 'Eclipsed', 'Frosted'
];
const ANIMALS_PLANTS = [
  'Coyote', 'Willow', 'Hawk', 'Pine', 'Badger', 'Sage', 'Fox', 'Juniper', 'Raven', 'Aspen',
  'Lynx', 'Fern', 'Wolf', 'Birch', 'Bear', 'Cactus', 'Bobcat', 'Oak', 'Panther', 'Maple',
  'Falcon', 'Cedar', 'Otter', 'Moss', 'Eagle', 'Thistle', 'Rabbit', 'Cottonwood', 'Owl', 'Prairie',
  'Alder', 'Bison', 'Buck', 'Cattail', 'Chipmunk', 'Clover', 'Cougar', 'Cricket', 'Crow', 'Deer',
  'Dogwood', 'Elk', 'Fawn', 'Fir', 'Foxglove', 'Grouse', 'Hare', 'Hemlock', 'Heron', 'Jay',
  'Larch', 'Lizard', 'Loon', 'Marten', 'Meadow', 'Mink', 'Mule', 'Nettle', 'Otter', 'Pheasant',
  'Poplar', 'Quail', 'Reed', 'Salamander', 'Salix', 'Sparrow', 'Spruce', 'Sumac', 'Tamarack', 'Tern',
  'Trout', 'Vole', 'Weasel', 'Wren', 'Yarrow', 'Yucca', 'Vireo', 'Plover', 'Pinecone', 'Sedge'
];

function getRandomName() {
  const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = ANIMALS_PLANTS[Math.floor(Math.random() * ANIMALS_PLANTS.length)];
  return `${adj} ${noun}`;
}

const CreateDesignPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [environmentPhoto, setEnvironmentPhoto] = useState(null);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [counties, setCounties] = useState([]);
  const [generatedDesign, setGeneratedDesign] = useState(null);
  const [designName, setDesignName] = useState('');
  const [loading, setLoading] = useState(false);
  const [generationReady, setGenerationReady] = useState(false);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [environmentPhotoId, setEnvironmentPhotoId] = useState(null);
  const [environmentPhotoUrl, setEnvironmentPhotoUrl] = useState(null);
  const [generatedDesignStorageUrl, setGeneratedDesignStorageUrl] = useState(null);
  const [designDocId, setDesignDocId] = useState(null);
  const [stylePreferenceDescription, setStylePreferenceDescription] = useState('');
  const [designA, setDesignA] = useState(null);
  const [designB, setDesignB] = useState(null);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [promptIterationIdA, setPromptIterationIdA] = useState('');
  const [promptIterationIdB, setPromptIterationIdB] = useState('');
  const [designAStorageUrl, setDesignAStorageUrl] = useState(null);
  const [designBStorageUrl, setDesignBStorageUrl] = useState(null);
  const [markdownAnalysisA, setMarkdownAnalysisA] = useState('');
  const [markdownAnalysisB, setMarkdownAnalysisB] = useState('');
  const [streamingPhaseA, setStreamingPhaseA] = useState(null);
  const [streamingPhaseB, setStreamingPhaseB] = useState(null);
  const [processingStep, setProcessingStep] = useState('');
  const [streamingView, setStreamingView] = useState('A');
  const [previewVariant, setPreviewVariant] = useState('A');
  const [showDesignPreview, setShowDesignPreview] = useState(false);
  const [effectivenessReportA, setEffectivenessReportA] = useState(null);
  const [effectivenessReportB, setEffectivenessReportB] = useState(null);
  const [scoringEffectiveness, setScoringEffectiveness] = useState(false);
  const [scoringEnvironmentSrc, setScoringEnvironmentSrc] = useState(null);

  // New state variables for image editing functionality
  const [openaiResponseIdA, setOpenaiResponseIdA] = useState('');
  const [openaiResponseIdB, setOpenaiResponseIdB] = useState('');
  const [originalPromptA, setOriginalPromptA] = useState('');
  const [originalPromptB, setOriginalPromptB] = useState('');
  const [showEditPrompt, setShowEditPrompt] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');
  const [editingDesign, setEditingDesign] = useState(false);
  const [editProcessingStep, setEditProcessingStep] = useState('');
  const [editedDesignUrl, setEditedDesignUrl] = useState(null);
  const [editedDesignStorageUrl, setEditedDesignStorageUrl] = useState(null);
  const [hasEdits, setHasEdits] = useState(false);
  const [editCompleted, setEditCompleted] = useState(false);
  const [selectedDesignForSubmission, setSelectedDesignForSubmission] = useState(null);
  const [editTimer, setEditTimer] = useState(0);
  const [editTimerInterval, setEditTimerInterval] = useState(null);

  // Monthly submission check
  const [hasMonthlyDesign, setHasMonthlyDesign] = useState(false);
  const [monthlyDesignId, setMonthlyDesignId] = useState(null);
  const [checkingMonthlyLimit, setCheckingMonthlyLimit] = useState(true);

  // Design element toggles
  const [designToggles, setDesignToggles] = useState({
    blobs: false,
    contourLines: false,
    speckledDots: false,
    geometricShapes: false,
    organicObjects: false,
    highContrast: false,
    limitedPalette: false,
    outlineStrokes: false,
    whiteSpace: false
  });

  // Track whether design elements are enabled
  const [designElementsEnabled, setDesignElementsEnabled] = useState(false);

  // Auto-resize textarea functionality
  const textareaRef = useRef(null);

  // Search states functionality
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [filteredStates, setFilteredStates] = useState([]);

  // Search counties functionality
  const [countySearchTerm, setCountySearchTerm] = useState('');
  const [showCountyDropdown, setShowCountyDropdown] = useState(false);
  const [filteredCounties, setFilteredCounties] = useState([]);

  // Track component mount to force re-check on back navigation
  const [mountKey, setMountKey] = useState(0);
  const workflowTopRef = useRef(null);

  const scrollWorkflowToTop = () => {
    scrollToTop({ smooth: false, delay: 0 });
    if (workflowTopRef.current) {
      workflowTopRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  };

  // Same route, different steps — scroll to top when step or preview mode changes
  useEffect(() => {
    scrollWorkflowToTop();
  }, [step, showDesignPreview]);

  // Force re-check when component mounts (handles back button navigation)
  useEffect(() => {
    setMountKey(prev => prev + 1);
    // Reset form state on mount to prevent stale data from back navigation
    setStep(1);
    setEnvironmentPhoto(null);
    setSelectedState('');
    setSelectedCounty('');
    setCounties([]);
    setStateSearchTerm('');
    setCountySearchTerm('');
    setFilteredStates([]);
    setFilteredCounties([]);
    setShowStateDropdown(false);
    setShowCountyDropdown(false);
    setGeneratedDesign(null);
    setDesignA(null);
    setDesignB(null);
    setSelectedDesign(null);
    setShowDesignPreview(false);
    setGenerationReady(false);
    setDesignName('');
    setEditedDesignUrl(null);
    setEditCompleted(false);
    setHasEdits(false);
    scrollWorkflowToTop();
  }, []);

  // Reusable: fetch from Firestore whether user has any design this month (used on load + before generate).
  const UNLIMITED_USERS = ['Co4cpjmwZQb8uWkGtVhqji1VQvq1', 'qyf7gBFJwCMnx9s7UzC69L9FLyf2'];
  const fetchMonthlyDesignStatus = async () => {
    if (!currentUser) return { hasMonthlyDesign: false, monthlyDesignId: null };
    if (UNLIMITED_USERS.includes(currentUser.uid)) return { hasMonthlyDesign: false, monthlyDesignId: null };

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const designsRef = collection(db, 'designs');
    const q = query(
      designsRef,
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(10)
    );
    const querySnapshot = await getDocs(q);

    for (const designDoc of querySnapshot.docs) {
      const designData = designDoc.data();
      if (!designData.createdAt) continue;
      let designDate;
      try {
        if (designData.createdAt.toDate) designDate = designData.createdAt.toDate();
        else if (designData.createdAt.seconds) designDate = new Date(designData.createdAt.seconds * 1000);
        else if (designData.createdAt instanceof Date) designDate = designData.createdAt;
        else continue;
        const isThisMonth = designDate.getMonth() === currentMonth && designDate.getFullYear() === currentYear;
        if (isThisMonth) return { hasMonthlyDesign: true, monthlyDesignId: designDoc.id };
      } catch (e) {
        console.error('Error processing design timestamp:', e);
      }
    }
    return { hasMonthlyDesign: false, monthlyDesignId: null };
  };

  // Check on mount so we show limit notice immediately if they already used their slot.
  useEffect(() => {
    if (!currentUser) {
      setCheckingMonthlyLimit(false);
      return;
    }
    let cancelled = false;
    setCheckingMonthlyLimit(true);
    fetchMonthlyDesignStatus()
      .then(({ hasMonthlyDesign, monthlyDesignId }) => {
        if (!cancelled) {
          setHasMonthlyDesign(hasMonthlyDesign);
          setMonthlyDesignId(monthlyDesignId);
        }
      })
      .catch((err) => console.error('Error checking monthly submission:', err))
      .finally(() => { if (!cancelled) setCheckingMonthlyLimit(false); });
    return () => { cancelled = true; };
  }, [currentUser, mountKey]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setEnvironmentPhoto(file);
      setMessage('');
    } else {
      setMessage('Please select a valid image file.');
    }
  };

  const handleStateChange = (e) => {
    const stateCode = e.target.value;
    setSelectedState(stateCode);
    setSelectedCounty('');
    if (stateCode) {
      setCounties(getCountiesForState(stateCode));
    } else {
      setCounties([]);
    }
  };

  const handleNextStep = async () => {
    // Always block if we already know they hit the limit (e.g. from initial load).
    if (hasMonthlyDesign) {
      setMessage('You have already used your one design this month.');
      return;
    }

    if (step === 1 && environmentPhoto && selectedState && stylePreferenceDescription.trim()) {
      // Fresh Firestore check on the upload step so back-button + regenerate can't bypass the limit.
      try {
        const { hasMonthlyDesign: hasLimit, monthlyDesignId: existingId } = await fetchMonthlyDesignStatus();
        if (hasLimit) {
          setHasMonthlyDesign(true);
          setMonthlyDesignId(existingId);
          setMessage('You have already used your one design this month.');
          return;
        }
      } catch (e) {
        console.error('Error checking monthly limit before generate:', e);
        setMessage('Could not verify design limit. Please try again.');
        return;
      }
      setStep(2);
      await uploadEnvironmentPhotoAndGenerateDesign();
    } else if (step === 1) {
      setMessage('Please upload a photo, select a state, and provide a style preference description.');
    }
  };

  const uploadEnvironmentPhotoAndGenerateDesign = async () => {
    setLoading(true);
    setGenerationReady(false);
    setShowDesignPreview(false);
    setProcessingStep('uploading_photo');
    setMessage('Uploading your environment photo...');

    // Initialize designDocRef at function scope
    let designDocRef = null;

    try {
      // Step 1: Create environment photo record in Firestore
      const envPhotoRef = await addDoc(collection(db, 'environment_photos'), {
        userId: currentUser.uid,
        state: selectedState,
        county: selectedCounty || 'N/A',
        description: stylePreferenceDescription.trim(),
        createdAt: serverTimestamp(),
        status: 'pending'
      });

      setEnvironmentPhotoId(envPhotoRef.id);

      // Step 2: Upload photo to Firebase Storage with date-based path
      setProcessingStep('uploading_to_storage');
      
      // Compress image before upload
      const compressionOptions = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };
      const compressedEnvironmentPhoto = await imageCompression(environmentPhoto, compressionOptions);
      
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const photoId = `${currentUser.uid}_${envPhotoRef.id}_env`;
      const photoRef = ref(storage, `environment_photos/${year}/${month}/${photoId}.png`);
      await uploadBytes(photoRef, compressedEnvironmentPhoto);
      const photoUrl = await getDownloadURL(photoRef);

      setEnvironmentPhotoUrl(photoUrl);

      // Step 3: Update environment photo record with URL
      await updateDoc(envPhotoRef, {
        photoUrl: photoUrl,
        status: 'uploaded'
      });

      setProcessingStep('generating_designs');
      setMessage('Photo uploaded! Generating your camo designs...');

      // Step 4: Update status to processing
      await updateDoc(envPhotoRef, {
        status: 'processing',
        processingStarted: serverTimestamp()
      });

      // Step 5: Call both OpenAI Cloud Functions in PARALLEL for A/B testing
      const idToken = await currentUser.getIdToken(true);

      setMarkdownAnalysisA('');
      setMarkdownAnalysisB('');
      setStreamingPhaseA('connecting');
      setStreamingPhaseB('connecting');
      
      // Prepare the request payload
      const requestPayload = {
        environmentPhotoUrl: photoUrl,
        state: selectedState,
        county: selectedCounty || 'N/A',
        userId: currentUser.uid,
        environmentPhotoId: envPhotoRef.id,
        stylePreferenceDescription: stylePreferenceDescription.trim(),
        ...(designElementsEnabled && { designToggles: designToggles })
      };

      const requestHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      };

      // Call both functions in parallel with streaming analysis
      let resultA, resultB;
      
      try {
        [resultA, resultB] = await Promise.all([
          streamCamoDesign(
            'https://generatecamodesigna-yepdp67tpq-uc.a.run.app',
            requestPayload,
            requestHeaders,
            createStreamingCallbacks('A')
          ),
          streamCamoDesign(
            'https://generatecamodesignb-yepdp67tpq-uc.a.run.app',
            requestPayload,
            requestHeaders,
            createStreamingCallbacks('B')
          )
        ]);

      } catch (parallelError) {
        console.error('Error in parallel function execution:', parallelError);
        
        // Try to get at least one design if one fails
        try {
          setMessage('One design failed, trying to generate remaining design...');
          
          // Try to get Design A if it failed
          if (!resultA) {
            console.log('Attempting to generate Design A as fallback...');
            resultA = await streamCamoDesign(
              'https://generatecamodesigna-yepdp67tpq-uc.a.run.app',
              requestPayload,
              requestHeaders,
              createStreamingCallbacks('A')
            );
            console.log('Fallback Design A generated:', resultA);
          }
          
          // Try to get Design B if it failed
          if (!resultB) {
            console.log('Attempting to generate Design B as fallback...');
            resultB = await streamCamoDesign(
              'https://generatecamodesignb-yepdp67tpq-uc.a.run.app',
              requestPayload,
              requestHeaders,
              createStreamingCallbacks('B')
            );
            console.log('Fallback Design B generated:', resultB);
          }
          
          // If we still don't have both designs, throw the original error
          if (!resultA && !resultB) {
            throw parallelError;
          }
          
        } catch (fallbackError) {
          console.error('Fallback generation also failed:', fallbackError);
          throw parallelError; // Throw the original error
        }
      }

      // Set state for both designs (only if they exist and have valid structure)
      if (resultA && resultA.designUrl) {
        setDesignA(resultA.designUrl);
        setPromptIterationIdA(resultA.promptIterationId || '');
        setMarkdownAnalysisA(sanitizePublicEnvironmentAnalysis(resultA.markdownAnalysis || ''));
        // Store OpenAI IDs for editing functionality
        if (resultA.openaiResponseId && typeof resultA.openaiResponseId === 'string') {
          setOpenaiResponseIdA(resultA.openaiResponseId);
        } else if (resultA.geminiResponseId && typeof resultA.geminiResponseId === 'string') {
          setOpenaiResponseIdA(resultA.geminiResponseId); // For compatibility
        }
        if (resultA.originalPrompt || resultA.prompt) {
          setOriginalPromptA(resultA.originalPrompt || resultA.prompt);
        }
      }

      if (resultB && resultB.designUrl) {
        setDesignB(resultB.designUrl);
        setPromptIterationIdB(resultB.promptIterationId || '');
        setMarkdownAnalysisB(sanitizePublicEnvironmentAnalysis(resultB.markdownAnalysis || ''));
        // Store OpenAI IDs for editing functionality
        if (resultB.openaiResponseId && typeof resultB.openaiResponseId === 'string') {
          setOpenaiResponseIdB(resultB.openaiResponseId);
        } else if (resultB.geminiResponseId && typeof resultB.geminiResponseId === 'string') {
          setOpenaiResponseIdB(resultB.geminiResponseId); // For compatibility
        }
        if (resultB.originalPrompt || resultB.prompt) {
          setOriginalPromptB(resultB.originalPrompt || resultB.prompt);
        }
      }

      setMessage('Designs generated! Uploading to storage...');

      // Step 6: Upload both designs to Firebase Storage in PARALLEL
      setProcessingStep('uploading_designs');
      let designAStorageUrl = null;
      let designBStorageUrl = null;
      
      try {
        console.log('Uploading designs to Firebase Storage in parallel...');
        
        const photoId = envPhotoRef.id;
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        
        // Prepare upload tasks for designs that exist and have valid URLs
        const uploadTasks = [];
        const downloadUrlTasks = [];
        
        if (resultA && resultA.designUrl) {
          try {
            const designAFile = dataURLtoFile(resultA.designUrl, `camo_design_${photoId}_a.png`);
            // Compress design A before upload
            const compressedDesignA = await imageCompression(designAFile, {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true
            });
            const designARef = ref(storage, `ai_gen/${year}/${month}/camo_design_${photoId}_a.png`);
            uploadTasks.push(uploadBytes(designARef, compressedDesignA).then(() => designARef));
            console.log('Added Design A to upload queue');
          } catch (error) {
            console.error('Failed to prepare Design A for upload:', error);
          }
        }
        
        if (resultB && resultB.designUrl) {
          try {
            const designBFile = dataURLtoFile(resultB.designUrl, `camo_design_${photoId}_b.png`);
            // Compress design B before upload
            const compressedDesignB = await imageCompression(designBFile, {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true
            });
            const designBRef = ref(storage, `ai_gen/${year}/${month}/camo_design_${photoId}_b.png`);
            uploadTasks.push(uploadBytes(designBRef, compressedDesignB).then(() => designBRef));
            console.log('Added Design B to upload queue');
          } catch (error) {
            console.error('Failed to prepare Design B for upload:', error);
          }
        }
        
        // Upload all designs in parallel
        const uploadedRefs = await Promise.all(uploadTasks);
        
        // Get download URLs in parallel
        const downloadUrls = await Promise.all(
          uploadedRefs.map(ref => getDownloadURL(ref))
        );
        
        // Assign URLs based on what was uploaded
        if (resultA && resultA.designUrl && downloadUrls.length > 0) {
          designAStorageUrl = downloadUrls[0];
          setDesignAStorageUrl(designAStorageUrl);
          console.log('Design A storage URL set:', designAStorageUrl);
        }
        
        if (resultB && resultB.designUrl) {
          if (resultA && resultA.designUrl && downloadUrls.length > 1) {
            designBStorageUrl = downloadUrls[1];
          } else if (downloadUrls.length > 0) {
            designBStorageUrl = downloadUrls[0];
          }
          setDesignBStorageUrl(designBStorageUrl);
          console.log('Design B storage URL set:', designBStorageUrl);
        }
        
        console.log('Designs uploaded to Firebase Storage:', {
          designA: designAStorageUrl,
          designB: designBStorageUrl
        });
      } catch (uploadError) {
        console.error('Failed to upload designs to Firebase Storage:', uploadError);
        // Continue with data URLs if upload fails
        if (resultA) designAStorageUrl = resultA.designUrl;
        if (resultB) designBStorageUrl = resultB.designUrl;
      }

      // Create design document immediately after generation
      try {
        setProcessingStep('creating_document');
        console.log('Creating design document in Firestore...');
        
        // Prompt iteration IDs are stored on the design doc; markdown_instructions
        // is maintained by cloud functions (refinement trigger).
        
        const designsRef = collection(db, 'designs');
        
        // Prepare the document data, only including fields that have valid values
        // NOTE: We explicitly DO NOT include: designAId, designBId, generationMethod, markdownInstructionsA, markdownInstructionsB
        // These fields are removed to use relational structure with markdown_instructions collection
        const designDocData = {
          name: '', // Will be updated on submission
          stylePreferenceDescription: stylePreferenceDescription.trim(),
          imageUrl: designAStorageUrl, // Default to design A, will be updated
          environmentPhotoId: envPhotoRef.id,
          environmentPhotoUrl: photoUrl,
          userId: currentUser.uid,
          designerName: currentUser.displayName || 'Anonymous',
          createdAt: serverTimestamp(),
          voteCount: 0,
          state: selectedState,
          county: selectedCounty || 'N/A',
          status: 'pending_selection', // Will be updated to 'submitted'
          designToggles: designToggles
        };
        
        // NOTE: prompt iteration IDs reference camo_prompt_iterations (global A/B chain)
        if (resultA && resultA.designUrl) {
          designDocData.designAUrl = designAStorageUrl;
          designDocData.markdownAnalysisA = sanitizePublicEnvironmentAnalysis(resultA.markdownAnalysis || '') || null;
          designDocData.promptIterationIdA = resultA.promptIterationId || null;
          if (resultA.openaiResponseId && typeof resultA.openaiResponseId === 'string') {
            designDocData.openaiResponseIdA = resultA.openaiResponseId;
          } else if (resultA.geminiResponseId && typeof resultA.geminiResponseId === 'string') {
            designDocData.openaiResponseIdA = resultA.geminiResponseId; // Store for compatibility
          }
          // Explicitly ensure these fields are NOT included:
          delete designDocData.designAId;
          delete designDocData.markdownInstructionsA;
        }
        
        if (resultB && resultB.designUrl) {
          designDocData.designBUrl = designBStorageUrl;
          designDocData.markdownAnalysisB = sanitizePublicEnvironmentAnalysis(resultB.markdownAnalysis || '') || null;
          designDocData.promptIterationIdB = resultB.promptIterationId || null;
          if (resultB.openaiResponseId && typeof resultB.openaiResponseId === 'string') {
            designDocData.openaiResponseIdB = resultB.openaiResponseId;
          } else if (resultB.geminiResponseId && typeof resultB.geminiResponseId === 'string') {
            designDocData.openaiResponseIdB = resultB.geminiResponseId; // Store for compatibility
          }
          // Explicitly ensure these fields are NOT included:
          delete designDocData.designBId;
          delete designDocData.markdownInstructionsB;
        }
        
        // Explicitly ensure generationMethod is NOT included
        delete designDocData.generationMethod;
        
        // Log the data being sent to Firestore for debugging
        console.log('Design document data to be created:', designDocData);
        console.log('Verifying unwanted fields are NOT present:', {
          hasDesignAId: 'designAId' in designDocData,
          hasDesignBId: 'designBId' in designDocData,
          hasGenerationMethod: 'generationMethod' in designDocData,
          hasMarkdownInstructionsA: 'markdownInstructionsA' in designDocData,
          hasMarkdownInstructionsB: 'markdownInstructionsB' in designDocData
        });
        
        // Validate that we have at least one design result before creating the document
        if (!resultA && !resultB) {
          throw new Error('No design results available to create document');
        }
        
        // Additional validation to ensure we have at least one valid design URL
        if (!designAStorageUrl && !designBStorageUrl) {
          throw new Error('No valid design URLs available to create document');
        }
        
        // Validate that we have at least one valid result
        if (!resultA && !resultB) {
          throw new Error('No valid design results available to create document');
        }
        
        // Log the final data structure before creating the document
        console.log('Final design document data:', JSON.stringify(designDocData, null, 2));
        
        // Final validation - ensure we have at least one required field
        if (!designDocData.stylePreferenceDescription || !designDocData.userId || !designDocData.state) {
          throw new Error('Missing required fields for design document');
        }
        
        designDocRef = await addDoc(designsRef, designDocData);
        
        setDesignDocId(designDocRef.id);
        console.log('Design document created with ID:', designDocRef.id);
      } catch (designError) {
        console.error('Failed to create design document:', designError);
        // Set designDocRef to null to prevent further errors
        designDocRef = null;
      }

      // Step 7: Update environment photo record with generated designs
      try {
        setProcessingStep('finalizing');
        console.log('Updating environment photo with generated designs:', envPhotoRef.id);
        
        const updateData = {
          status: 'completed',
          processingCompleted: serverTimestamp(),
        };
        
        // Only add designDocId if the design document was created successfully
        if (designDocRef && designDocRef.id) {
          updateData.designDocId = designDocRef.id;
        } else {
          console.warn('Design document was not created, updating environment photo without designDocId');
        }
        
        await updateDoc(envPhotoRef, updateData);
        console.log('Environment photo updated successfully');
      } catch (updateError) {
        console.error('Failed to update environment photo with generated designs:', updateError);
        // Try to update with just the basic status to avoid leaving it in 'processing' state
        try {
          await updateDoc(envPhotoRef, {
            status: 'completed',
            processingCompleted: serverTimestamp()
          });
          console.log('Environment photo status updated to completed');
        } catch (fallbackError) {
          console.error('Failed to update environment photo status:', fallbackError);
        }
      }

      // Set dynamic success message based on what was generated
      const designCount = [resultA, resultB].filter(Boolean).length;
      if (designCount === 2) {
        setMessage('Both designs generated! Please select your preferred design.');
      } else if (designCount === 1) {
        setMessage('One design generated successfully! Please proceed with your design.');
      } else {
        setMessage('Design generation completed. Please review your results.');
      }
      
      // If design document creation failed but we have designs, show a warning
      if (!designDocRef && (resultA || resultB)) {
        console.warn('Design document creation failed, but designs are available for viewing');
        setMessage('Designs generated but there was an issue saving to database. You can still view and select designs.');
      }

      setGenerationReady(true);

    } catch (error) {
      console.error('Error in design generation process:', error);
      
      // Update environment photo status to failed if we have an ID
      if (environmentPhotoId) {
        try {
          await updateDoc(doc(db, 'environment_photos', environmentPhotoId), {
            status: 'failed',
            error: error.message
          });
        } catch (updateError) {
          console.error('Failed to update error status:', updateError);
        }
      }
      
      setMessage('Failed to generate designs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDesignSelection = (designVariant) => {
    setSelectedDesign(designVariant);
    setGeneratedDesign(designVariant === 'A' ? designA : designB);
  };

  const handleImageEdit = async () => {
    if (!editPrompt.trim()) {
      setMessage('Please enter your edit request.');
      return;
    }

    if (!selectedDesign) {
      setMessage('Please select a design before editing.');
      return;
    }

    // Get the current design image and original prompt based on selected design
    const currentImage = selectedDesign === 'A' ? designA : designB;
    const originalPrompt = selectedDesign === 'A' ? originalPromptA : originalPromptB;
    
    if (!currentImage) {
      setMessage('Image editing is not available. Please generate a design first.');
      return;
    }

    setEditingDesign(true);
    setEditProcessingStep('processing_edit');
    setMessage('Processing your edit request...');
    
    // Start the edit timer
    setEditTimer(0);
    const interval = setInterval(() => {
      setEditTimer(prev => prev + 1);
    }, 1000);
    setEditTimerInterval(interval);

    try {
      const idToken = await currentUser.getIdToken(true);

      // Call the image editing cloud function
      const editResponse = await fetch('https://editcamodesign-yepdp67tpq-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          editPrompt: editPrompt.trim(),
          currentImage: currentImage, // Send the current design image
          originalPrompt: originalPrompt, // Send the original generation prompt
          userId: currentUser.uid,
          environmentPhotoId: environmentPhotoId,
          selectedDesign: selectedDesign
        })
      });

      if (!editResponse.ok) {
        const errorText = await editResponse.text();
        throw new Error(`Edit failed: ${editResponse.status} - ${errorText}`);
      }

      const editResult = await editResponse.json();
      
      if (editResult.editedImageUrl) {
        // Stop the edit timer
        if (editTimerInterval) {
          clearInterval(editTimerInterval);
          setEditTimerInterval(null);
        }
        
        setEditedDesignUrl(editResult.editedImageUrl);
        setEditProcessingStep('uploading_edited');
        setMessage('Edit completed! Uploading to storage...');
        setEditCompleted(true);
        
        try {
          // Upload the edited image to Firebase Storage through frontend (like Design A/B)
          const editedImageFile = dataURLtoFile(editResult.editedImageUrl, `camo_design_${environmentPhotoId}_${selectedDesign.toLowerCase()}_edited.png`);
          
          // Compress edited image before upload
          const compressedEditedImage = await imageCompression(editedImageFile, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true
          });
          
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          
          const editedImageRef = ref(storage, `ai_gen/${year}/${month}/camo_design_${environmentPhotoId}_${selectedDesign.toLowerCase()}_edited.png`);
          
          await uploadBytes(editedImageRef, compressedEditedImage);
          const editedImageStorageUrl = await getDownloadURL(editedImageRef);
          
          setEditedDesignStorageUrl(editedImageStorageUrl);
          setEditProcessingStep('completed');
          setMessage('Edit completed successfully! Your edited design is ready.');
          setEditCompleted(true);
          
          // Update the design document with the edited version
          await updateDoc(doc(db, 'designs', designDocId), {
            editedImageUrl: editedImageStorageUrl,
            editPrompt: editPrompt.trim(),
            editedAt: serverTimestamp(),
            hasEdits: true
          });
          
          setHasEdits(true);
          
        } catch (uploadError) {
          console.error('Failed to upload edited image to Firebase Storage:', uploadError);
          // Fallback to base64 data URL if upload fails
          setEditedDesignStorageUrl(editResult.editedImageUrl);
          setEditCompleted(true);
          
          // Update the design document with the edited version
          await updateDoc(doc(db, 'designs', designDocId), {
            editedImageUrl: editResult.editedImageUrl,
            editPrompt: editPrompt.trim(),
            editedAt: serverTimestamp(),
            hasEdits: true
          });
          
          setHasEdits(true);
        }
      } else {
        throw new Error('No edited image received from the service');
      }

    } catch (error) {
      console.error('Error editing image:', error);
      setMessage(`Failed to edit image: ${error.message}`);
    } finally {
      // Stop the edit timer
      if (editTimerInterval) {
        clearInterval(editTimerInterval);
        setEditTimerInterval(null);
      }
      setEditingDesign(false);
    }
  };

  const handleSubmitDesign = async () => {
    // Prevent submission if monthly limit already reached (back button protection)
    if (hasMonthlyDesign) {
      setMessage('You have already submitted a design this month.');
      navigate(`/gallery/${monthlyDesignId}`, { replace: true });
      return;
    }

    if (!designName.trim()) {
      setMessage('Please enter a name for your design.');
      return;
    }

    if (!selectedDesign) {
      setMessage('Please select a design before submitting.');
      return;
    }

    // Auto-select edited version if user edited but hasn't made a selection
    if (editCompleted && !selectedDesignForSubmission) {
      setSelectedDesignForSubmission('edited');
    }

    if (!designDocId) {
      setMessage('Design document not found. Please try generating the design again.');
      return;
    }

    setUploading(true);
    try {
      // Update the existing design document with user selection and description
      if (designDocId) {
        // Determine the final image URL based on user selection
        let finalImageUrl;
        const finalChoice = editCompleted && editedDesignUrl 
          ? (selectedDesignForSubmission || 'edited')
          : 'original';
        
        if (editCompleted && finalChoice === 'edited') {
          finalImageUrl = editedDesignStorageUrl || editedDesignUrl;
        } else {
          finalImageUrl = selectedDesign === 'A' ? designAStorageUrl : designBStorageUrl;
        }
        
        if (!finalImageUrl) {
          throw new Error('No valid image URL found for submission');
        }
        
        console.log('Final image URL for submission:', finalImageUrl);
        console.log('Edit completed:', editCompleted);
        console.log('Final choice:', finalChoice);
        console.log('Edited design storage URL:', editedDesignStorageUrl);
        console.log('Edited design URL:', editedDesignUrl);
        
        // Prepare update data - explicitly exclude unwanted fields
        const updateData = {
          name: designName.trim(),
          imageUrl: finalImageUrl,
          selectedVariant: selectedDesign, // "A" or "B"
          selectedPromptIterationId: selectedDesign === 'A'
            ? (promptIterationIdA || null)
            : (promptIterationIdB || null),
          finalDesignChoice: finalChoice, // "original" or "edited"
          status: 'submitted',
          submittedAt: serverTimestamp(),
          promptRefinementStatus: 'pending',
          
          // Store all image URLs for proper display across pages
          designAUrl: designAStorageUrl,
          designBUrl: designBStorageUrl,
          editedImageUrl: editCompleted ? (editedDesignStorageUrl || editedDesignUrl) : null,
          
          // Store the edit information if applicable
          editPrompt: editCompleted ? editPrompt : null,
          editedAt: editCompleted ? serverTimestamp() : null,
          hasEdits: editCompleted
        };
        
        // Explicitly ensure unwanted fields are NOT included in update
        delete updateData.designAId;
        delete updateData.designBId;
        delete updateData.generationMethod;
        delete updateData.markdownInstructionsA;
        delete updateData.markdownInstructionsB;
        
        await updateDoc(doc(db, 'designs', designDocId), updateData);

        console.log('Design document updated with user selection:', designDocId);

        // Async prompt refinement (non-blocking)
        const idToken = await currentUser.getIdToken();
        triggerDesignRefinement({
          designId: designDocId,
          userId: currentUser.uid,
          idToken,
        });
      } else {
        throw new Error('Design document not found');
      }

      // Update user document with lastDesignCreatedAt timestamp
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userDocRef, {
          lastDesignCreatedAt: serverTimestamp()
        });
      } catch (userUpdateError) {
        console.error('Error updating user lastDesignCreatedAt:', userUpdateError);
        // Don't fail the whole submission if this fails
      }

      // Immediately mark that user has submitted this month to prevent back button bypass
      setHasMonthlyDesign(true);
      setMonthlyDesignId(designDocId);

      setMessage('Design submitted successfully!');
      setUploading(false);
      
      // Use replace instead of navigate to prevent back button from returning here
      navigate(`/gallery/${designDocId}`, { replace: true });
    } catch (error) {
      console.error('Error submitting design:', error);
              setMessage('Failed to submit design. Please try again.');
        setUploading(false);
      }
    };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setShowDesignPreview(false);
    setGenerationReady(false);
      setMessage('');
    }
  };

  const handleViewDesign = () => {
    setShowDesignPreview(true);
  };

  // Handle state search
  const handleStateSearch = (searchTerm) => {
    setStateSearchTerm(searchTerm);
    if (searchTerm.trim() === '') {
      setFilteredStates([]);
      setShowStateDropdown(false);
    } else {
      const filtered = US_STATES.filter(state =>
        state.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        state.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStates(filtered);
      setShowStateDropdown(true);
    }
  };

  // Handle county search
  const handleCountySearch = (searchTerm) => {
    setCountySearchTerm(searchTerm);
    if (searchTerm.trim() === '') {
      setFilteredCounties([]);
      setShowCountyDropdown(false);
    } else {
      const allCounties = getCountiesForState(selectedState);
      const filtered = allCounties.filter(county =>
        county.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCounties(filtered);
      setShowCountyDropdown(true);
    }
  };

  // Handle design toggle changes
  const handleToggleChange = (toggleName) => {
    setDesignToggles(prev => ({
      ...prev,
      [toggleName]: !prev[toggleName]
    }));
  };

  // Reset toggles to default values
  const resetTogglesToDefault = () => {
    setDesignToggles({
      blobs: false,
      contourLines: false,
      speckledDots: false,
      geometricShapes: false,
      organicObjects: false,
      highContrast: false,
      limitedPalette: false,
      outlineStrokes: false,
      whiteSpace: false
    });
  };

  // Select state
  const selectState = (state) => {
    setSelectedState(state.code);
    setStateSearchTerm(state.name);
    setShowStateDropdown(false);
    setSelectedCounty('');
    setCountySearchTerm('');
    setCounties(getCountiesForState(state.code));
  };

  // Select county
  const selectCounty = (county) => {
    setSelectedCounty(county);
    setCountySearchTerm(county);
    setShowCountyDropdown(false);
  };

  // Handle clicks outside dropdowns
  const handleClickOutside = (event) => {
    if (!event.target.closest('.search-container')) {
      setShowStateDropdown(false);
      setShowCountyDropdown(false);
    }
  };

  // Add click outside listener
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Cleanup edit timer on component unmount
  useEffect(() => {
    return () => {
      if (editTimerInterval) {
        clearInterval(editTimerInterval);
      }
    };
  }, [editTimerInterval]);

  // Auto-resize textarea when content changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [stylePreferenceDescription]);

  // Prefer local environment photo for scoring (avoids Firebase Storage CORS on canvas)
  useEffect(() => {
    if (environmentPhoto) {
      const objectUrl = URL.createObjectURL(environmentPhoto);
      setScoringEnvironmentSrc(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setScoringEnvironmentSrc(environmentPhotoUrl);
  }, [environmentPhoto, environmentPhotoUrl]);

  // Score camo effectiveness once both designs and environment photo are ready
  useEffect(() => {
    if (!scoringEnvironmentSrc || !designA || !designB || loading) {
      return;
    }

    let cancelled = false;

    const runScoring = async () => {
      setScoringEffectiveness(true);
      setEffectivenessReportA(null);
      setEffectivenessReportB(null);

      try {
        const [reportA, reportB] = await Promise.all([
          scoreCamoEffectiveness(scoringEnvironmentSrc, designA),
          scoreCamoEffectiveness(scoringEnvironmentSrc, designB),
        ]);

        if (!cancelled) {
          setEffectivenessReportA(reportA);
          setEffectivenessReportB(reportB);
        }
      } catch (error) {
        console.error('Camo effectiveness scoring failed:', error);
      } finally {
        if (!cancelled) {
          setScoringEffectiveness(false);
        }
      }
    };

    runScoring();
    return () => {
      cancelled = true;
    };
  }, [scoringEnvironmentSrc, designA, designB, loading]);

  const recommendedVariant =
    effectivenessReportA && effectivenessReportB
      ? effectivenessReportA.overall >= effectivenessReportB.overall ? 'A' : 'B'
      : null;

  const generationComplete = generationReady && !loading && designA && designB;
  const showGenerationPanel = loading || (generationComplete && !showDesignPreview);
  const loadingProgressPercent = getLoadingProgressPercent(processingStep, generationComplete);

  const activeStreamingMarkdown = streamingView === 'A' ? markdownAnalysisA : markdownAnalysisB;
  const showEnvironmentPanel = generationComplete || !!markdownAnalysisA || !!markdownAnalysisB;

  const createStreamingCallbacks = (variant) => ({
    onStatus: ({ phase }) => {
      if (variant === 'A') {
        setStreamingPhaseA(phase);
      } else {
        setStreamingPhaseB(phase);
      }

      const postGenerateSteps = ['uploading_designs', 'creating_document', 'finalizing'];
      setProcessingStep((prev) => {
        if (postGenerateSteps.includes(prev)) return prev;
        if (phase === 'analyzing') return 'generating_designs';
        if (phase === 'generating_image') return 'ai_generation';
        return prev;
      });
    },
    onAnalysisDelta: (text) => {
      if (variant === 'A') {
        setStreamingPhaseA((prev) => (prev === 'connecting' || prev === 'waiting' ? 'analyzing' : prev));
        setMarkdownAnalysisA((prev) => sanitizePublicEnvironmentAnalysis(prev + text));
      } else {
        setStreamingPhaseB((prev) => (prev === 'connecting' || prev === 'waiting' ? 'analyzing' : prev));
        setMarkdownAnalysisB((prev) => sanitizePublicEnvironmentAnalysis(prev + text));
      }
    },
    onAnalysisComplete: (fullText) => {
      const sanitized = sanitizePublicEnvironmentAnalysis(fullText);
      if (variant === 'A') {
        setMarkdownAnalysisA(sanitized);
      } else {
        setMarkdownAnalysisB(sanitized);
      }
    },
    onComplete: () => {
      if (variant === 'A') {
        setStreamingPhaseA('complete');
      } else {
        setStreamingPhaseB('complete');
      }
    }
  });

  // Get current month name for display
  const getCurrentMonthName = () => {
    return new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="create-design-page">
      <div className="page-header">
        <h1>Create Your Camo Design</h1>
        <p>Submit your camo pattern to the community</p>
      </div>

      {checkingMonthlyLimit ? (
        <div className="create-design-workflow">
          <div className="loading-section">
            <h3>Checking monthly submission status...</h3>
          </div>
        </div>
      ) : hasMonthlyDesign ? (
        <div className="create-design-workflow">
          <div className="monthly-limit-notice">
            <h2>Monthly Design Limit Reached</h2>
            <p>You've already submitted a design for {getCurrentMonthName()}.</p>
            <p>Each user can create one design per month. Come back next month to create a new design!</p>
            <p style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', fontSize: '0.95rem' }}>
              <strong>Want unlimited generations?</strong> Send an email to <a href="mailto:ops@cultcamo.com" style={{ color: '#ff6b35', textDecoration: 'underline' }}>ops@cultcamo.com</a> to become an affiliated member and get unlimited pattern creation!
            </p>
            <div className="limit-notice-actions">
              {monthlyDesignId && (
                <button 
                  className="view-design-btn"
                  onClick={() => navigate(`/gallery/${monthlyDesignId}`)}
                >
                  View Your {getCurrentMonthName().split(' ')[0]} Design
                </button>
              )}
              <button 
                className="back-to-design-btn"
                onClick={() => navigate('/design')}
              >
                Back to Design Studio
              </button>
              <button 
                className="gallery-btn"
                onClick={() => navigate('/gallery')}
              >
                Browse Gallery
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {message && step === 1 && <div className="message">{message}</div>}

          <div className="create-design-workflow" ref={workflowTopRef}>
        {/* Step 1: Upload Photo and Select Location */}
        {step === 1 && (
          <div className="step-content">
            
            <div className="upload-section">
              <h3>Upload Environment Photo</h3>
              <p>Provide a photo of the environment where your camouflage pattern will be used</p>
              
              <div 
                className="file-upload-area"
                onClick={() => fileInputRef.current.click()}
                style={{ backgroundImage: `url(${ditherArizona})` }}
              >
                {environmentPhoto ? (
                  <div className="uploaded-photo">
                    <img src={URL.createObjectURL(environmentPhoto)} alt="Environment" />
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <p>Click to upload environment photo</p>
                    <p className="upload-hint">Supports JPG or PNG</p>
                  </div>
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <br></br>
              <div className="form-group">
                <label>Style Preference Description *</label>
                <textarea
                  ref={textareaRef}
                  value={stylePreferenceDescription}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) { // Limit to ~3-4 paragraphs
                      setStylePreferenceDescription(e.target.value);
                    }
                  }}
                  placeholder="Describe your style preferences for the camouflage pattern (colors, shapes, textures, etc.)"
                  rows="1"
                  style={{ resize: 'none', overflow: 'hidden' }}
                  maxLength="500"
                  required
                />
                <div className="character-count">
                  {stylePreferenceDescription.length}/500 characters
                </div>
                <p className="input-hint">This helps our AI understand your style preferences better (required)</p>
              </div>
            </div>

            <div className="location-section">
              <h3>Select Location</h3>
              <p>Choose the state and county where this photo was taken</p>
              
              <div className="location-inputs">
                <div className="form-group">
                  <label>State</label>
                  <div className="search-container">
                    <input
                      type="text"
                      value={stateSearchTerm}
                      onChange={(e) => handleStateSearch(e.target.value)}
                      placeholder="Search state"
                    />
                    {showStateDropdown && (
                      <div className="dropdown">
                        {filteredStates.map(state => (
                          <div key={state.code} onClick={() => selectState(state)}>
                            {state.name} ({state.code})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>County (optional)</label>
                  <div className={`search-container ${!selectedState ? 'disabled' : ''}`}>
                    <input
                      type="text"
                      value={countySearchTerm}
                      onChange={(e) => handleCountySearch(e.target.value)}
                      placeholder={selectedState ? "Search county (optional)" : "Select a state first"}
                      disabled={!selectedState}
                    />
                    {showCountyDropdown && (
                      <div className="dropdown">
                        {filteredCounties.map(county => (
                          <div key={county} onClick={() => selectCounty(county)}>
                            {county}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="design-toggles-section">
              <div className="design-elements-header">
                <h3>Design Elements</h3>
                <button 
                  type="button" 
                  className={`toggle-design-elements-btn ${designElementsEnabled ? 'enabled' : 'disabled'}`}
                  onClick={() => setDesignElementsEnabled(!designElementsEnabled)}
                >
                  {designElementsEnabled ? 'Disable Design Elements' : 'Use Design Elements'}
                </button>
              </div>
              
              {designElementsEnabled ? (
                <>
                  <p>Customize which design elements to include in your camouflage pattern. This will provide further direction to the AI system.</p>
                  
                  <div className="toggles-header">
                    <button 
                      type="button" 
                      className="reset-toggles-btn"
                      onClick={resetTogglesToDefault}
                    >
                      Reset to Defaults
                    </button>
                  </div>
                  
                  <div className="toggles-grid">
                    <div 
                      className={`toggle-item ${designToggles.blobs ? 'active' : ''}`}
                      onClick={() => handleToggleChange('blobs')}
                    >
                      <div className="toggle-label">
                        <span className="toggle-text">Blobs</span>
                      </div>
                      <span className="toggle-description">Soft, organic blob shapes</span>
                    </div>

                    <div 
                      className={`toggle-item ${designToggles.contourLines ? 'active' : ''}`}
                      onClick={() => handleToggleChange('contourLines')}
                    >
                      <div className="toggle-label">
                        <span className="toggle-text">Contour Lines</span>
                      </div>
                      <span className="toggle-description">Terrain-following lines</span>
                    </div>

                    <div 
                      className={`toggle-item ${designToggles.speckledDots ? 'active' : ''}`}
                      onClick={() => handleToggleChange('speckledDots')}
                    >
                      <div className="toggle-label">
                        <span className="toggle-text">Speckled Dots</span>
                      </div>
                      <span className="toggle-description">Small scattered dots</span>
                    </div>

                    <div 
                      className={`toggle-item ${designToggles.geometricShapes ? 'active' : ''}`}
                      onClick={() => handleToggleChange('geometricShapes')}
                    >
                      <div className="toggle-label">
                        <span className="toggle-text">Geometric Shapes</span>
                      </div>
                      <span className="toggle-description">Angular, structured forms</span>
                    </div>

                    <div 
                      className={`toggle-item ${designToggles.organicObjects ? 'active' : ''}`}
                      onClick={() => handleToggleChange('organicObjects')}
                    >
                      <div className="toggle-label">
                        <span className="toggle-text">Organic Objects</span>
                      </div>
                      <span className="toggle-description">Natural, flowing shapes</span>
                    </div>

                    <div 
                      className={`toggle-item ${designToggles.highContrast ? 'active' : ''}`}
                      onClick={() => handleToggleChange('highContrast')}
                    >
                      <div className="toggle-label">
                        <span className="toggle-text">High Contrast</span>
                      </div>
                      <span className="toggle-description">Bold light/dark differences</span>
                    </div>

                    <div 
                      className={`toggle-item ${designToggles.limitedPalette ? 'active' : ''}`}
                      onClick={() => handleToggleChange('limitedPalette')}
                    >
                      <div className="toggle-label">
                        <span className="toggle-text">Limited Palette</span>
                      </div>
                      <span className="toggle-description">Restricted color selection</span>
                    </div>

                    <div 
                      className={`toggle-item ${designToggles.outlineStrokes ? 'active' : ''}`}
                      onClick={() => handleToggleChange('outlineStrokes')}
                    >
                      <div className="toggle-label">
                        <span className="toggle-text">Outline Strokes</span>
                      </div>
                      <span className="toggle-description">Defined edge lines</span>
                    </div>

                    <div 
                      className={`toggle-item ${designToggles.whiteSpace ? 'active' : ''}`}
                      onClick={() => handleToggleChange('whiteSpace')}
                    >
                      <div className="toggle-label">
                        <span className="toggle-text">White Space</span>
                      </div>
                      <span className="toggle-description">Strategic empty areas</span>
                    </div>
                  </div>
                </>
              ) : (
                <p>If not used, the AI will use its own creative judgment to generate the best camouflage pattern for your environment.</p>
              )}
            </div>

            <div className="step-actions">
              <button 
                className="next-btn"
                onClick={handleNextStep}
                disabled={!environmentPhoto || !selectedState || !stylePreferenceDescription.trim()}
              >
                Generate Design
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Generated Design with Name and Submit */}
        {step === 2 && (
          <div className="step-content">
            <h2>{showGenerationPanel ? 'Creating your design' : 'Your design'}</h2>
            
            {showGenerationPanel ? (
              <div className="loading-section loading-section-compact">
                <div className="progress-rail-wrapper">
                  <div className="progress-rail-track" aria-hidden="true">
                    <div
                      className="progress-rail-fill"
                      style={{ width: `${loadingProgressPercent}%` }}
                    />
                  </div>
                  <div className="progress-rail" aria-label="Generation progress">
                    {LOADING_STEPS.map((railStep) => {
                      const stepStatus = getLoadingStepStatus(railStep.id, processingStep, generationComplete);
                      return (
                        <div
                          key={railStep.id}
                          className={`progress-rail-step ${stepStatus}`}
                          title={railStep.label}
                        >
                          <span className="progress-rail-dot" />
                          <span className="progress-rail-label">{railStep.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {showEnvironmentPanel && (
                <div className="streaming-analysis-section streaming-single">
                  <div className="streaming-analysis-header-row">
                    <h4>Environment</h4>
                    <VariantToggle
                      value={streamingView}
                      onChange={setStreamingView}
                    />
                  </div>
                  <div className="streaming-analysis-panel">
                    {activeStreamingMarkdown && (
                      <div className="streaming-analysis-content scrollbar-hidden">
                        <SimpleMarkdownRenderer>
                          {activeStreamingMarkdown}
                        </SimpleMarkdownRenderer>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {generationComplete && (
                  <div className="generation-complete-footer">
                    <button
                      type="button"
                      className="view-design-btn view-design-btn-loading"
                      onClick={handleViewDesign}
                    >
                      View Designs
                    </button>
                  </div>
                )}
              </div>
            ) : designA && designB ? (
              <div className="design-preview-section">
                {/* Step 1: Initial Design Selection */}
                {!selectedDesign && (
                  <div className="design-selection design-selection-mobile">
                    <h3>Pick your pattern</h3>

                    <VariantToggle
                      value={previewVariant}
                      onChange={setPreviewVariant}
                      scoreA={effectivenessReportA?.overall}
                      scoreB={effectivenessReportB?.overall}
                      recommended={recommendedVariant}
                    />

                    <div className="design-preview-hero">
                      <img
                        src={previewVariant === 'A' ? designA : designB}
                        alt={`Design ${previewVariant}`}
                      />
                    </div>

                    <CamoEffectivenessReport
                      environmentImageUrl={scoringEnvironmentSrc}
                      patternImageUrl={previewVariant === 'A' ? designA : designB}
                      variant={previewVariant}
                      mode="summary"
                      report={previewVariant === 'A' ? effectivenessReportA : effectivenessReportB}
                      loading={scoringEffectiveness}
                      isRecommended={recommendedVariant === previewVariant}
                    />

                    <button
                      type="button"
                      className="select-btn select-btn-full"
                      onClick={() => handleDesignSelection(previewVariant)}
                    >
                      Use Design {previewVariant}
                    </button>
                  </div>
                )}

                {/* Step 2: Edit & Finalize Selected Design */}
                {selectedDesign && (
                  <div className="design-finalization-container">
                    <div className="selected-design-header">
                      <h3>Your Selected Design</h3>
                    </div>

                    {/* Current Design Display */}
                    <div className="current-design-showcase">
                      <div className="showcase-grid">
                        <div className="showcase-item original">
                          <div className="showcase-label">
                            <span className="label-badge">Original</span>
                            <h4>Design {selectedDesign}</h4>
                          </div>
                          <img src={selectedDesign === 'A' ? designA : designB} alt={`Design ${selectedDesign}`} />
                          {!editingDesign && editedDesignUrl && (
                            <button 
                              className={`version-select-btn ${!selectedDesignForSubmission || selectedDesignForSubmission === 'original' ? 'selected' : ''}`}
                              onClick={() => setSelectedDesignForSubmission('original')}
                            >
                              {selectedDesignForSubmission === 'original' ? '✓ Use Original' : 'Use Original'}
                            </button>
                          )}
                        </div>

                        {editedDesignUrl && (
                          <div className="showcase-item edited">
                            <div className="showcase-label">
                              <span className="label-badge edited-badge">Edited</span>
                              <h4>Your Custom Edit</h4>
                            </div>
                            <img src={editedDesignUrl} alt="Edited Design" />
                            {!editingDesign && (
                              <button 
                                className={`version-select-btn ${selectedDesignForSubmission === 'edited' ? 'selected' : ''}`}
                                onClick={() => setSelectedDesignForSubmission('edited')}
                              >
                                {selectedDesignForSubmission === 'edited' ? '✓ Use Edited' : 'Use Edited'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {editedDesignUrl && editPrompt && (
                        <div className="edit-info-banner">
                          <strong>Edit Applied:</strong> {editPrompt}
                        </div>
                      )}
                    </div>

                    {/* Edit Interface - Only show if no edit has been completed yet */}
                    {((selectedDesign === 'A' && designA) || (selectedDesign === 'B' && designB)) && !editingDesign && !editCompleted && (
                      <div className="edit-interface">
                        <h4>Want to customize this design?</h4>
                        <p className="edit-hint">
                          Describe any changes you'd like to make to your selected design. You get one edit.
                        </p>
                        
                        <div className="edit-form-compact">
                          <textarea
                            value={editPrompt}
                            onChange={(e) => setEditPrompt(e.target.value)}
                            placeholder="e.g., 'add more contrast', 'make it darker', 'add organic shapes'"
                            maxLength="200"
                            rows="2"
                          />
                          <div className="edit-form-footer">
                            <span className="character-count-inline">{editPrompt.length}/200</span>
                            <button
                              type="button"
                              className="apply-edit-btn"
                              onClick={handleImageEdit}
                              disabled={!editPrompt.trim()}
                            >
                              Apply Edit
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Edit Progress */}
                    {editingDesign && (
                      <div className="edit-progress-section">
                        <h4 className="edit-progress-title">Processing Your Edit</h4>
                        
                        <div className="edit-progress-steps">
                          <div className={`progress-step ${editProcessingStep === 'processing_edit' ? 'active' : editProcessingStep === 'uploading_edited' || editProcessingStep === 'completed' ? 'completed' : ''}`}>
                            <div className="step-icon">1</div>
                            <div className="step-text">
                              <strong>AI Processing</strong>
                              <span>
                                Applying your edits to the design
                                {editProcessingStep === 'processing_edit' && editTimer > 0 && (
                                  <span className="timer-display"> • {editTimer}s</span>
                                )}
                              </span>
                              {editProcessingStep === 'processing_edit' && (
                                <span className="patience-note">Be patient - takes up to 1 minute</span>
                              )}
                            </div>
                          </div>
                          
                          <div className={`progress-step ${editProcessingStep === 'uploading_edited' ? 'active' : editProcessingStep === 'completed' ? 'completed' : ''}`}>
                            <div className="step-icon">2</div>
                            <div className="step-text">
                              <strong>Saving Edit</strong>
                              <span>Uploading your edited design</span>
                            </div>
                          </div>
                          
                          <div className={`progress-step ${editProcessingStep === 'completed' ? 'active completed' : ''}`}>
                            <div className="step-icon">3</div>
                            <div className="step-text">
                              <strong>Complete</strong>
                              <span>Your edited design is ready!</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!((selectedDesign === 'A' && designA) || (selectedDesign === 'B' && designB)) && (
                      <div className="edit-unavailable-notice">
                        <p>⚠️ Image editing is not available for this design.</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedDesign && !editingDesign && (
                  <>
                    <div className="design-details">
                      <p><strong>Location:</strong> {selectedCounty && selectedCounty !== 'N/A' ? `${selectedCounty} County, ` : ''}{US_STATES.find(s => s.code === selectedState)?.name}</p>
                      <p><strong>Generated:</strong> {new Date().toLocaleDateString()}</p>
                      <p><strong>Selected Design:</strong> Design {selectedDesign}{editedDesignUrl && selectedDesignForSubmission === 'edited' ? ' (Edited Version)' : ''}</p>
                    </div>
                    <br></br>
                    <div className="name-section">
                      <h3>Name Your Design</h3>
                      <div className="form-group">
                        <input
                          type="text"
                          value={designName}
                          onChange={(e) => setDesignName(e.target.value)}
                          placeholder="Enter a name for your design"
                          maxLength="50"
                          required
                          disabled={editCompleted && !selectedDesignForSubmission}
                        />
                        <button
                          type="button"
                          style={{ 
                            marginTop: '0.5rem', 
                            marginBottom: '0.5rem', 
                            background: '#f6c12b', 
                            color: '#000', 
                            border: 'none', 
                            borderRadius: '6px', 
                            padding: '0.5rem 1rem', 
                            fontWeight: 600, 
                            cursor: (editCompleted && !selectedDesignForSubmission) ? 'not-allowed' : 'pointer', 
                            opacity: (editCompleted && !selectedDesignForSubmission) ? 0.5 : 1 
                          }}
                          onClick={() => setDesignName(getRandomName())}
                          disabled={editCompleted && !selectedDesignForSubmission}
                        >
                          Generate Random Name
                        </button>
                        <p className="input-hint">Give your design a memorable name (max 50 characters)</p>
                      </div>
                    </div>

                    <div className="step-actions">
                      <button 
                        className={`submit-btn ${uploading ? 'submitting' : ''}`}
                        onClick={handleSubmitDesign}
                        disabled={uploading || !designName.trim() || (editCompleted && !selectedDesignForSubmission)}
                      >
                        {uploading ? 'Submitting...' : 'Submit Design'}
                      </button>
                      
                      {uploading && (
                        <div className="submit-feedback">
                          <p>Saving your design to the community...</p>
                          <p className="submit-hint">This will only take a moment</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="error-section">
                <p>Failed to generate designs. Please try again.</p>
                <button className="back-btn" onClick={handleBack}>
                  Go Back
                </button>
              </div>
            )}
          </div>
        )}
          </div>
        </>
      )}
    </div>
  );
};

export default CreateDesignPage; 